import { Button, HStack, Switch, Text, VStack, useDisclosure } from '@chakra-ui/react';
import { get } from 'lodash-es';
import { useSetMeetingConfig } from '@services/query-services/people.query-service';
import { useUnscheduleUserMeetings } from '@services/query-services/lambda.query-service';
import { useUserStore } from '@store/app.store';
import { useStyledToast } from '@util/hooks';
import { SFInfoIcon } from '@assets/icons';
import ControlledTooltip from '@components/general/controlled-tooltip.component';
import StyledModal from '@components/general/modals/styled-modal.component';

const getAutoScheduleConfig = (property) => (state) =>
  get(state, [property, 'meeting_config', 'auto_schedule'], null);

export default function AutoSchedulingSection() {
  const user = useUserStore((state) => state?.user);
  const getSessionId = useUserStore((state) => state?.getSessionId);
  const setUser = useUserStore((state) => state.setUser);
  const isEnterpriseUser = useUserStore((state) => state?.isEnterpriseUser)();
  const userAutoSchedule = useUserStore(getAutoScheduleConfig('user'));
  const tenantAutoSchedule = useUserStore(getAutoScheduleConfig('tenant'));
  const toast = useStyledToast();
  const { mutateAsync: setMeetingConfig, isLoading: isSetting } = useSetMeetingConfig();
  const { mutateAsync: unscheduleMeetings, isLoading: isUnscheduling } =
    useUnscheduleUserMeetings();
  const autoSchedule = userAutoSchedule === null ? tenantAutoSchedule : userAutoSchedule;
  const disclosure = useDisclosure();

  const handleAutoScheduleChange = async (value) => {
    await setMeetingConfig(
      {
        id: user?.id,
        body: { ...(user?.meeting_config || {}), auto_schedule: value }
      },
      {
        onSuccess: () => {
          setUser({ ...user, meeting_config: { ...user?.meeting_config, auto_schedule: value } });
          toast({
            title: 'Auto Scheduling Updated Successfully',
            status: 'success',
            isClosable: true
          });
        },
        onError: () =>
          toast({
            title: 'Something went wrong while updating auto scheduling, please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true
          })
      }
    );
  };

  const handleBulkUnschedule = async () => {
    try {
      await unscheduleMeetings({ user, session_id: getSessionId() });
      toast({
        title: 'Successfully unscheduled FORA from all meetings',
        status: 'success',
        isClosable: true
      });
      disclosure.onClose();
    } catch (error) {
      toast({
        title: 'Something went wrong while unscheduling meetings, please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <VStack
      align='stretch'
      spacing={4}
    >
      <HStack gap={3}>
        <Switch
          id='auto-scheduling'
          isChecked={autoSchedule}
          onChange={(e) => handleAutoScheduleChange(e?.target?.checked)}
          isDisabled={isSetting}
        />
        <Text
          as='label'
          htmlFor='auto-scheduling'
          variant='body-500-md'
          color='surface.onSurfaceVariant'
          cursor='pointer'
        >
          Automatically schedule FORA to join all future videoconference calls
        </Text>
        <ControlledTooltip
          label={`Turning this ON will auto-schedule FORA to join all of your meetings scheduled going
          forward. Existing meetings on your calendar will not be automatically scheduled.`}
        >
          {(tooltipProps) => (
            <span>
              <SFInfoIcon {...tooltipProps} />
            </span>
          )}
        </ControlledTooltip>
      </HStack>

      {!isEnterpriseUser && (
        <>
          <StyledModal
            disclosure={disclosure}
            openButton={
              <Button
                variant='reskinPrimary'
                size='sm'
                isLoading={isUnscheduling}
              >
                Bulk Unschedule
              </Button>
            }
            title='Bulk Unschedule'
            modalProps={{ size: 'md' }}
          >
            <VStack
              spacing={4}
              p={4}
              align='stretch'
            >
              <Text>Are you sure you want to unschedule FORA from all of your meetings?</Text>
              <HStack
                spacing={3}
                justify='flex-end'
              >
                <Button
                  variant='reskinOutline'
                  onClick={() => disclosure.onClose()}
                >
                  Cancel
                </Button>
                <Button
                  variant='reskinPrimary'
                  onClick={handleBulkUnschedule}
                  isLoading={isUnscheduling}
                >
                  Yes
                </Button>
              </HStack>
            </VStack>
          </StyledModal>
        </>
      )}
    </VStack>
  );
}
